import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Editor from '@monaco-editor/react';
import { fetchData } from '../../helpers/apiUtils';

interface TemplateValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface TemplateEditorProps {
  open: boolean;
  filename: string | null; // null for creating new template
  onClose: () => void;
  onSaved: () => void;
}

const SAMPLE_CONTENT = `
<h2 style="color: #333; margin-bottom: 15px;">Beispiel-Überschrift</h2>
<p style="color: #555; line-height: 1.6;">
  Dies ist ein Beispieltext, der den E-Mail-Inhalt simuliert.
  Hier würde der eigentliche Newsletter-Content erscheinen.
</p>
<p style="margin-top: 20px;">
  <a href="#" style="color: #0024FF; text-decoration: underline; font-weight: bold;">
    Jetzt zum Webinar anmelden &rarr;
  </a>
</p>
`;

const TemplateEditor: React.FC<TemplateEditorProps> = ({ open, filename, onClose, onSaved }) => {
  const [html, setHtml] = useState('');
  const [originalHtml, setOriginalHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [previewWidth, setPreviewWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [validation, setValidation] = useState<TemplateValidation | null>(null);
  const [newFilename, setNewFilename] = useState('');

  const isNewTemplate = filename === null;
  const hasChanges = html !== originalHtml;

  // Load template content when opening
  useEffect(() => {
    if (open && filename) {
      loadTemplate(filename);
    } else if (open && !filename) {
      // New template - start with starter template
      const starterTemplate = getStarterTemplate();
      setHtml(starterTemplate);
      setOriginalHtml('');
      setNewFilename('');
      validateHtml(starterTemplate);
    }
  }, [open, filename]);

  const loadTemplate = async (fname: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchData<{ filename: string; html: string; lastModified: string }>(
        `/api/webinar-mailings/templates/${encodeURIComponent(fname)}`,
      );
      if (response.success && response.data) {
        setHtml(response.data.html);
        setOriginalHtml(response.data.html);
        validateHtml(response.data.html);
      } else {
        setError(response.error || 'Fehler beim Laden des Templates');
      }
    } catch (err) {
      setError('Fehler beim Laden des Templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validateHtml = async (content: string) => {
    try {
      const response = await fetchData<TemplateValidation>(
        '/api/webinar-mailings/templates/validate',
        {
          method: 'POST',
          body: JSON.stringify({ html: content }),
        },
      );
      if (response.success && response.data) {
        setValidation(response.data);
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const handleHtmlChange = (value: string | undefined) => {
    const newHtml = value || '';
    setHtml(newHtml);
    // Debounce validation
    const timer = setTimeout(() => validateHtml(newHtml), 500);
    return () => clearTimeout(timer);
  };

  const handleSave = async () => {
    if (!validation?.valid) return;

    setSaving(true);
    setError(null);

    try {
      const endpoint = isNewTemplate
        ? '/api/webinar-mailings/templates'
        : `/api/webinar-mailings/templates/${encodeURIComponent(filename!)}`;

      const body = isNewTemplate
        ? { filename: newFilename, html, updated_by: 'dashboard-user' }
        : { html, updated_by: 'dashboard-user' };

      const response = await fetchData<{ message: string }>(endpoint, {
        method: isNewTemplate ? 'POST' : 'PUT',
        body: JSON.stringify(body),
      });

      if (response.success) {
        onSaved();
        onClose();
      } else {
        setError(response.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Fehler beim Speichern');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('Ungespeicherte Änderungen verwerfen?')) {
        return;
      }
    }
    onClose();
  };

  // Preview HTML with sample content
  const previewHtml = useMemo(() => {
    return html.replace('{{CONTENT}}', SAMPLE_CONTENT);
  }, [html]);

  const getStarterTemplate = () => {
    return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="de">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>*|MC:SUBJECT|*</title>
    <style type="text/css">
        body { margin: 0; padding: 0; background: #f5f5f5; font-family: Arial, sans-serif; }
        .container { max-width: 640px; margin: 0 auto; background: #ffffff; }
        .content { padding: 30px; }
        .footer { padding: 20px 30px; font-size: 12px; color: #888; }
    </style>
</head>
<body>
    <div style="display:none;">*|MC_PREVIEW_TEXT|*</div>

    <table width="100%" bgcolor="#f5f5f5" cellpadding="0" cellspacing="0">
        <tr>
            <td align="center" style="padding: 20px 0;">
                <!-- Header -->
                <table class="container" width="640" cellpadding="0" cellspacing="0">
                    <tr>
                        <td style="padding: 20px 30px; text-align: center;">
                            <a href="*|ARCHIVE|*" style="color: #888; font-size: 12px;">Online ansehen</a>
                        </td>
                    </tr>
                </table>

                <!-- Content -->
                <table class="container" width="640" bgcolor="#ffffff" cellpadding="0" cellspacing="0">
                    <tr>
                        <td class="content" style="padding: 30px;">
                            {{CONTENT}}
                        </td>
                    </tr>
                </table>

                <!-- Footer -->
                <table class="container" width="640" cellpadding="0" cellspacing="0">
                    <tr>
                        <td class="footer" style="padding: 20px 30px; font-size: 12px; color: #888;">
                            <a href="*|UNSUB|*" style="color: #888;">Abmelden</a>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {isNewTemplate ? 'Neues Template erstellen' : `Template bearbeiten: ${filename}`}
      </DialogTitle>
      <DialogContent sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {isNewTemplate && (
              <TextField
                label="Dateiname"
                value={newFilename}
                onChange={(e) => setNewFilename(e.target.value)}
                placeholder="mein-template.html"
                fullWidth
                sx={{ mb: 2 }}
                helperText="Muss mit .html enden"
                error={newFilename !== '' && !newFilename.endsWith('.html')}
              />
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label="Code" />
                <Tab label="Vorschau" />
              </Tabs>
            </Box>

            <Box flex={1} sx={{ minHeight: 0 }}>
              {activeTab === 0 ? (
                <Editor
                  height="100%"
                  language="html"
                  theme="vs-dark"
                  value={html}
                  onChange={handleHtmlChange}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    wordWrap: 'on',
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                  }}
                />
              ) : (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}>
                    <ToggleButtonGroup
                      value={previewWidth}
                      exclusive
                      onChange={(_, v) => v && setPreviewWidth(v)}
                      size="small"
                    >
                      <ToggleButton value="desktop">
                        <DesktopWindowsIcon sx={{ mr: 0.5 }} /> Desktop (640px)
                      </ToggleButton>
                      <ToggleButton value="mobile">
                        <PhoneIphoneIcon sx={{ mr: 0.5 }} /> Mobile (375px)
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      backgroundColor: '#e0e0e0',
                      overflow: 'auto',
                      p: 2,
                    }}
                  >
                    <iframe
                      srcDoc={previewHtml}
                      style={{
                        width: previewWidth === 'desktop' ? '640px' : '375px',
                        height: '100%',
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                      }}
                      title="Template Preview"
                      sandbox="allow-same-origin"
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Validation Status */}
            {validation && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Validierung:
                </Typography>
                {validation.errors.length === 0 && validation.warnings.length === 0 ? (
                  <Box display="flex" alignItems="center" color="success.main">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography>Alle Prüfungen bestanden</Typography>
                  </Box>
                ) : (
                  <>
                    {validation.errors.map((err, i) => (
                      <Box
                        key={`err-${i}`}
                        display="flex"
                        alignItems="center"
                        color="error.main"
                        mb={0.5}
                      >
                        <ErrorIcon sx={{ mr: 1, fontSize: 18 }} />
                        <Typography variant="body2">{err}</Typography>
                      </Box>
                    ))}
                    {validation.warnings.map((warn, i) => (
                      <Box
                        key={`warn-${i}`}
                        display="flex"
                        alignItems="center"
                        color="warning.main"
                        mb={0.5}
                      >
                        <WarningIcon sx={{ mr: 1, fontSize: 18 }} />
                        <Typography variant="body2">{warn}</Typography>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            saving ||
            loading ||
            !validation?.valid ||
            (isNewTemplate && (!newFilename || !newFilename.endsWith('.html')))
          }
        >
          {saving ? <CircularProgress size={20} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditor;
